import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Row,
  Switch,
  Radio,
  message as antMessage
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import IBAN from 'iban';
import moment from 'moment';
import useXmlPattern from '../../utils/xmlPattern';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import PrintSignSepaModal from './PrintSignSepaModal';
import useContractsContext from './ContractsContext';

const { Option } = Select;

const UpdateBankInfo = ({ contract, shownContract }) => {
  const { t } = useTranslation();
  const xmlPattern = useXmlPattern();
  const { dispatchAPI, user } = useAuthContext();
  const { forceRefresh, setForceRefresh } = useContractsContext();
  const { message } = useErrorMessage();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [bics, setBics] = useState([]);
  const [enumGenders, setEnumGenders] = useState([]);

  const signSepa = async () => {
    setDownloading(true);
    try {
      let mailToRegister;
      if (!contract.customer.email) {
        form.getFieldsError().some((item) => {
          if (item.errors.length !== 0) {
            return antMessage.error(t('contracts.modal.email_error'));
          }
          mailToRegister = form.getFieldValue('email');
          return true;
        });
      }
      if (mailToRegister || contract.customer.email) {
        const { data } = await dispatchAPI('POST', {
          url: `/contracts/signSepa`,
          body: { email: mailToRegister, contractId: contract._id }
        });
        if (data) {
          setModalVisible(false);
          setForceRefresh(!forceRefresh);
          antMessage.success(t('contracts.modal.email_send_success'));
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setDownloading(false);
  };

  const printSepa = async () => {
    setIsDownloading(true);
    try {
      const { data, headers } = await dispatchAPI('GET', {
        url: `/contracts/download/${contract._id}?documents=sepa`,
        responseType: 'blob'
      });
      const fileName = headers['content-disposition'].substring(
        headers['content-disposition'].indexOf('=') + 1,
        headers['content-disposition'].length
      );
      const blob = new Blob([data], {
        contentType: 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}`;
      a.click();
      antMessage.success(t('export.messages.success'));
      form.resetFields();
      setModalVisible(false);
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsDownloading(false);
  };

  const getBics = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: 'bics' });
      setBics(data.map((b) => b.code));
    } catch (e) {
      setBics([]);
      message(e);
    }
  };

  const getEnumGenders = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/contracts/enums' });
      setEnumGenders(data.genders);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const statusAccepted = [
    'QUOTE',
    'ACTIVE',
    'UNPAID_1',
    'UNPAID_2',
    'FORMAL_NOTICE',
    'ACTIVE_AFTER_SUSPENSION'
  ];

  const onClose = () => {
    form.resetFields();
    setVisible(false);
  };

  const updateBankInfo = async (body) => {
    setIsDownloading(true);
    try {
      const response = await dispatchAPI('PATCH', {
        url: `/contracts/bank/${shownContract._id}`,
        body
      });
      if (response.status === 200) {
        antMessage.success(t('contracts.form.bank_info_success'));
      }
      onClose();
      setModalVisible(true);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsDownloading(false);
  };

  const validateBankInfo = async (values) => {
    await updateBankInfo({
      ...values,
      type: contract?.type,
      status: contract?.status
    });
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const handleChange = () => {
    if (!form.getFieldValue('rib_not_subscriber')) {
      form.resetFields([
        'rib_address',
        'rib_address_details',
        'rib_city',
        'rib_civility',
        'rib_date_of_birth',
        'rib_first_name',
        'rib_last_name',
        'rib_postal_code'
      ]);
    }
  };

  useEffect(() => {
    (async () => {
      await getBics();
      await getEnumGenders();
    })();
    // eslint-disable-next-line
  }, []);

  const isReductScreen = useMediaQuery({ query: '(max-width: 1450px)' });

  return (
    <div
      id="update-bank-info-btn"
      style={{ float: 'right', position: 'relative', top: -35 }}
    >
      {contract?.payment_type === 'DIRECTDEBIT' &&
        statusAccepted.map((status) => {
          if (status === contract?.status) {
            return (
              <Button
                value={status}
                key={status}
                size="small"
                type="link"
                onClick={showDrawer}
              >
                <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                  {t('buttons.edit_bank_info')}
                </span>
                <EditOutlined />
              </Button>
            );
          }
          return '';
        })}
      <Drawer
        title={t('contracts.form.bank_info_drawer')}
        placement="right"
        closable
        onClose={onClose}
        open={visible}
        size={430}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={validateBankInfo}
          onValuesChange={(_, values) => handleChange(values)}
          initialValues={{
            ...shownContract,
            rib_date_of_birth: shownContract?.rib_date_of_birth
              ? moment(shownContract?.rib_date_of_birth)
              : null
          }}
        >
          <Form.Item
            name={['bank_name']}
            label={t('validate_quote.form.bank_name')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['bank_address']}
            label={t('validate_quote.form.bank_address')}
            rules={[{ message: t('errors.form.REQUIRED') }]}
          >
            <Input style={{ width: 260 }} />
          </Form.Item>
          <Form.Item
            name={['bank_postal_code']}
            label={t('validate_quote.form.bank_postal_code')}
            rules={[{ message: t('errors.form.REQUIRED') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['bank_city']}
            label={t('validate_quote.form.bank_city')}
            rules={[{ message: t('errors.form.REQUIRED') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['iban']}
            label={t('validate_quote.form.iban')}
            hasFeedback={IBAN}
            rules={[
              { required: true, message: t('errors.form.REQUIRED') },
              () => ({
                validator(_, value) {
                  if (IBAN.isValid(value)) return Promise.resolve();
                  // eslint-disable-next-line prefer-promise-reject-errors
                  return Promise.reject('IBAN invalide');
                }
              })
            ]}
          >
            <Input style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item
            name={['bic']}
            label={t('validate_quote.form.bic')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
          >
            <Select
              showSearch
              style={{ width: '100%', textAlign: 'left', borderRadius: 5 }}
            >
              {bics.map((bic) => (
                <Option value={bic} key={bic}>
                  {bic}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={['rib_not_subscriber']}
            label={t('validate_quote.form.rib_owner')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t('quotes.form.switch.no')}
              unCheckedChildren={t('quotes.form.switch.yes')}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue('rib_not_subscriber') ? (
                <>
                  <p>{t('validate_quote.form.rib_not_subscriber')}</p>
                  <Form.Item name={['rib_type']} rules={[{ required: true }]}>
                    <Radio.Group
                      style={{
                        flexFlow: 'row wrap',
                        marginBottom: 10,
                        marginTop: 10
                      }}
                      defaultValue="INDIVIDUAL"
                    >
                      <Radio value="INDIVIDUAL">
                        {t('validate_quote.form.radio_individual')}
                      </Radio>
                      <Radio value="COMPANY">
                        {t(
                          `validate_quote.form.radio_${
                            contract?.type === 'ASSOCIATION'
                              ? 'asso'
                              : 'company'
                          }`
                        )}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {getFieldValue('rib_type') === 'COMPANY' ? (
                    <Form.Item
                      name={['rib_company_name']}
                      label={t(
                        `validate_quote.form.rib_${
                          contract?.type === 'ASSOCIATION' ? 'asso' : 'company'
                        }_name`
                      )}
                      rules={[{ required: true }, ...xmlPattern]}
                    >
                      <Input />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item
                        name={['rib_civility']}
                        label={t('validate_quote.form.rib_civility')}
                        rules={[{ required: true }]}
                      >
                        <Select>
                          {enumGenders &&
                            enumGenders.map((item) => (
                              <Option key={item} value={item}>
                                {t(`validate_quote.form.${item}`)}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={['rib_first_name']}
                        label={t('validate_quote.form.rib_first_name')}
                        rules={[{ required: true }, ...xmlPattern]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name={['rib_last_name']}
                        label={t('validate_quote.form.rib_last_name')}
                        rules={[{ required: true }, ...xmlPattern]}
                      >
                        <Input />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item
                    name={['rib_address']}
                    label={t('validate_quote.form.rib_address')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_address_details']}
                    label={t('validate_quote.form.rib_address_details')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_postal_code']}
                    label={t('validate_quote.form.rib_postal_code')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_city']}
                    label={t('validate_quote.form.rib_city')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>
          <Row style={{ marginTop: 25 }}>
            <Button loading={isDownloading} type="primary" htmlType="submit">
              {t('buttons.validate')}
              <CheckOutlined />
            </Button>
            <Button danger type="link" onClick={onClose}>
              {t('buttons.cancel')}
              <CloseOutlined />
            </Button>
          </Row>
        </Form>
      </Drawer>
      <PrintSignSepaModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        printSepa={printSepa}
        signSepa={signSepa}
        isDownloading={isDownloading}
        downloading={downloading}
        contract={contract}
        user={user}
      />
    </div>
  );
};

UpdateBankInfo.propTypes = {
  contract: PropTypes.shape({
    payment_type: PropTypes.string,
    customer: PropTypes.shape({
      email: PropTypes.string,
      _id: PropTypes.string
    }),
    _id: PropTypes.string,
    rib_type: PropTypes.string,
    rib_date_of_birth: PropTypes.instanceOf(Date),
    type: PropTypes.string,
    status: PropTypes.string
  }).isRequired,
  shownContract: PropTypes.shape({
    _id: PropTypes.string,
    rib_date_of_birth: PropTypes.string
  })
};

UpdateBankInfo.defaultProps = { shownContract: {} };

export default UpdateBankInfo;
